import { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import AdvancedTransferRequestNotification from '../AdvancedTransferRequestNotification'

const TransferNotificationListener = () => {
  const { user } = useContext(AuthContext)
  const socketManager = useContext(SocketContext)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [notificationData, setNotificationData] = useState(null)

  useEffect(() => {
    if (!user) return

    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)

    // Cache para evitar notificações duplicadas
    const notificationCache = new Set()

    const handleTransferRequest = data => {
      const {
        action,
        ticketId,
        requestingUserName,
        requestingUserId,
        currentUserId,
        // message,
        ticket
      } = data

      if (action === 'transfer-request') {
        // Criar chave única para esta notificação
        const notificationKey = `${ticketId}-${requestingUserId}-${action}`

        // Se já processamos esta notificação, ignorar
        if (notificationCache.has(notificationKey)) {
          console.log(
            `[TransferNotification] Notificação duplicada ignorada: ${notificationKey}`
          )
          return
        }

        // Adicionar ao cache
        notificationCache.add(notificationKey)
        console.log(
          `[TransferNotification] Processando notificação: ${notificationKey}`
        )

        // Limpar cache após 30 segundos
        setTimeout(() => {
          notificationCache.delete(notificationKey)
        }, 30000)

        // Verificar se os dados do ticket existem
        const contactName =
          ticket && ticket.contact && ticket.contact.name
            ? ticket.contact.name
            : 'Contato não identificado'

        // const queueName =
        //   ticket && ticket.queue && ticket.queue.name
        //     ? ticket.queue.name
        //     : 'Não definido'

        // Se o usuário atual é o dono do ticket, mostrar notificação interativa
        // if (currentUserId && parseInt(currentUserId) === parseInt(user.id)) {
        //   toast(
        //     ({ closeToast }) => (
        //       <AdvancedTransferRequestNotification
        //         ticketId={ticketId}
        //         requestingUserName={requestingUserName}
        //         requestingUserId={requestingUserId}
        //         contactName={contactName}
        //         currentQueueName={queueName}
        //         currentQueueId={ticket?.queue?.id}
        //         onClose={closeToast}
        //       />
        //     ),
        //     {
        //       position: 'top-right',
        //       autoClose: false, // Não fechar automaticamente
        //       hideProgressBar: true,
        //       closeOnClick: false,
        //       pauseOnHover: true,
        //       draggable: false,
        //       closeButton: false, // Usar o botão customizado
        //     }
        //   )
        // }

        if (currentUserId && parseInt(currentUserId) === parseInt(user.id)) {
          setNotificationData(data)
          setDialogOpen(true)
        }

        // Para todos os outros usuários, mostrar notificação informativa
        else {
          // Se é admin/supervisor, mostrar notificação com mais detalhes
          if (user.profile === 'admin' || user.profile === 'supervisor') {
            toast.warning(
              `Solicitação de transferência: ${requestingUserName} quer assumir o atendimento #${ticketId} (${contactName})`,
              {
                position: 'top-right',
                autoClose: 10000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
              }
            )
          }
          // Para usuários comuns que não são donos do ticket, mostrar notificação informativa
          else {
            toast.info(
              `📋 Solicitação de transferência: ${requestingUserName} solicitou o atendimento #${ticketId} (${contactName})`,
              {
                position: 'top-right',
                autoClose: 8000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
              }
            )
          }
        }
      }
    }

    const handleNotification = data => {
      if (data.action === 'transfer-request') {
        // Se o usuário atual é o dono do ticket E tem excludeUserId, ignorar (evitar duplicata)
        if (
          data.excludeUserId &&
          parseInt(data.excludeUserId) === parseInt(user.id)
        ) {
          return // Não mostrar notificação duplicada
        }
        handleTransferRequest(data)
      } else if (data.action === 'transfer-accepted') {
        const queueInfo =
          data.queueChanged && data.newQueueName
            ? ` e movido para o departamento ${data.newQueueName}`
            : data.newQueueName && !data.queueChanged
              ? ` (mantido no departamento ${data.newQueueName})`
              : ''

        toast.success(
          `✅ ${data.respondingUserName
          } aceitou sua solicitação de transferência do ticket #${data.ticketId
          } (${data.ticket?.contact?.name || 'Contato não identificado'
          })${queueInfo}`,
          {
            position: 'top-right',
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          }
        )
      } else if (data.action === 'transfer-rejected') {
        toast.error(
          `❌ ${data.respondingUserName
          } rejeitou sua solicitação de transferência do ticket #${data.ticketId
          } (${data.ticket?.contact?.name || 'Contato não identificado'})`,
          {
            position: 'top-right',
            autoClose: 8000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          }
        )
      }
    }

    // Escutar apenas um evento para todas as notificações
    socket.on(`company-${companyId}-notification`, handleNotification)

    return () => {
      socket.off(`company-${companyId}-notification`, handleNotification)
    }
  }, [user, socketManager])

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setNotificationData(null)
  }

  // return null // Este componente não renderiza nada
  return (
    <>
      {notificationData && (
        <AdvancedTransferRequestNotification
          isOpen={dialogOpen}
          ticketId={notificationData.ticketId}
          requestingUserName={notificationData.requestingUserName}
          requestingUserId={notificationData.requestingUserId}
          requestingUserProfile={notificationData.requestingUserProfile}
          requestingUserSuper={notificationData.requestingUserSuper}
          requestingUserQueues={notificationData.requestingUserQueues}
          requestedTargetQueueId={notificationData.requestedTargetQueueId}
          contactName={notificationData.ticket?.contact?.name}
          currentQueueName={notificationData.ticket?.queue?.name}
          currentQueueId={notificationData.ticket?.queue?.id}
          onClose={handleCloseDialog}
        />
      )}
    </>
  )
}

export default TransferNotificationListener
